import React, { useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { Grid } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from "react-router-dom";

import api from '../../utils/webcall';
import AccessController from '../../components/access_controller';
import UserProfile from '../../components/User';
import { GreenTickIcon, RedCrossIcon } from '../../components/commons/icons';

function ViewBillsList(props) {
    const [filter, setFilter] = useState('all'); // 'all', 'approved', 'unapproved'
    const isMobile = useMediaQuery('(max-width:600px)');
    const { showErrorMessage } = props;
    const [billListData, setBillListData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const username = UserProfile.getName();

    useEffect(()=>{
        api.get(`/pos/bill/list/`).then((res) => {
            let data = res.data;
            // Sort bills in reverse order by id
            const sortedBills = data['bills'].slice().sort((a, b) => b.id - a.id);
            setBillListData(sortedBills);
            setLoading(false);
        }).catch((error) => {
            console.log(error);
            showErrorMessage(String(error));
        });
    },[]);

    // Filter bills based on filter state
    const filteredBills = billListData ? billListData.filter(bill => {
        if (filter === 'approved') return bill.approved;
        if (filter === 'unapproved') return !bill.approved;
        return true;
    }) : [];

    return (
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '100vh', background: (theme) => theme.palette.background.default }}>
            <Grid item xs={12} sm={12} md={10} lg={8} xl={7}>
                <AccessController onlyAdmin={false}>
                    <Paper elevation={4} sx={{ p: { xs: 1, sm: 2, md: 4 }, borderRadius: 3, backgroundColor: (theme) => theme.palette.background.paper }}>
                        <Grid container direction="column" alignItems="center" spacing={2}>
                            <Grid item sx={{ textAlign: 'center' }}>
                                <h2 style={{ marginBottom: 8, fontSize: '1.3rem' }}>DIVASH | ADMIN</h2>
                                <i style={{ fontSize: '1rem' }}>Welcome {username}</i>
                                <b style={{ fontSize: '1.1rem' }}>View Bill</b>
                            </Grid>
                            <Grid item sx={{ width: '100%' }}>
                                <Grid
                                    container
                                    spacing={1}
                                    justifyContent={isMobile ? "center" : "flex-end"}
                                    direction={isMobile ? "column" : "row"}
                                    alignItems={isMobile ? "stretch" : "center"}
                                    sx={{ mb: 2 }}
                                >
                                    <Grid item xs={12} sm="auto">
                                        <Button
                                            fullWidth={isMobile}
                                            variant={filter === 'all' ? 'contained' : 'outlined'}
                                            size="small"
                                            onClick={() => setFilter('all')}
                                        >
                                            All
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm="auto">
                                        <Button
                                            fullWidth={isMobile}
                                            variant={filter === 'approved' ? 'contained' : 'outlined'}
                                            size="small"
                                            onClick={() => setFilter('approved')}
                                        >
                                            Approved
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm="auto">
                                        <Button
                                            fullWidth={isMobile}
                                            variant={filter === 'unapproved' ? 'contained' : 'outlined'}
                                            size="small"
                                            onClick={() => setFilter('unapproved')}
                                        >
                                            Unapproved
                                        </Button>
                                    </Grid>
                                </Grid>
                                {loading ? (
                                    <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 120 }}>
                                        <CircularProgress size={32} />
                                    </Grid>
                                ) : (
                                    filteredBills && filteredBills.length > 0 ? (
                                        isMobile ? (
                                            <Grid container spacing={2}>
                                                {filteredBills.map((item) => (
                                                    <Grid item xs={12} key={item.id}>
                                                        <Card variant="outlined" sx={{ mb: 1 }}>
                                                            <CardContent>
                                                                <Typography variant="subtitle2" color="text.secondary">Bill #{item.id}</Typography>
                                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                                    <b>Approved:</b> {item.approved ? <GreenTickIcon /> : <RedCrossIcon />}
                                                                </Typography>
                                                                <Typography variant="body2"><b>Date:</b> {item.submitted_date}</Typography>
                                                                <Typography variant="body2"><b>By:</b> {item.user === null ? 'null' : `${item.user.first_name} ${item.user.last_name}`}</Typography>
                                                                <Typography variant="body2"><b>Amount:</b> {item.total_amount.toFixed(2)} {item.currency}</Typography>
                                                                <Typography variant="body2"><b>Items:</b> {item.number_of_items}</Typography>
                                                                <Button
                                                                    variant='outlined'
                                                                    aria-label={`View bill ${item.id}`}
                                                                    size="small"
                                                                    sx={{ mt: 1 }}
                                                                    onClick={() => navigate(`/bill/view/${item.id}`)}
                                                                >
                                                                    View
                                                                </Button>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        ) : (
                                            <TableContainer component={Paper} sx={{ overflowX: 'auto', backgroundColor: (theme) => theme.palette.background.paper }}>
                                                <Table stickyHeader sx={{ minWidth: { xs: 350, sm: 600, md: 700 } }} size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Bill #</TableCell>
                                                            <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Approved</TableCell>
                                                            <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Bill Submitted On</TableCell>
                                                            <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Submitted By</TableCell>
                                                            <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Total Amount</TableCell>
                                                            <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Number of Items</TableCell>
                                                            <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {filteredBills.map((item) => (
                                                            <TableRow key={item.id}>
                                                                <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>{item.id}</TableCell>
                                                                <TableCell align="center">{item.approved ? <GreenTickIcon /> : <RedCrossIcon />}</TableCell>
                                                                <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>{item.submitted_date}</TableCell>
                                                                <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>{item.user === null  ? 'null' : `${item.user.first_name} ${item.user.last_name}`}</TableCell>
                                                                <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>{item.total_amount.toFixed(2)} {item.currency}</TableCell>
                                                                <TableCell align="center" sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>{item.number_of_items}</TableCell>
                                                                <TableCell align="center">
                                                                    <Button
                                                                        variant='outlined'
                                                                        aria-label={`View bill ${item.id}`}
                                                                        size="small"
                                                                        onClick={() => navigate(`/bill/view/${item.id}`)}
                                                                    >
                                                                        View
                                                                    </Button>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )
                                    ) : (
                                        <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>No bills found.</Typography>
                                    )
                                )}
                            </Grid>
                        </Grid>
                    </Paper>
                </AccessController>
            </Grid>
        </Grid>
    );  
}

export default ViewBillsList;